import { motion } from "framer-motion";
import {
  FileText,
  GitBranch,
  Factory,
  ScanSearch,
  PackageCheck,
  Ship,
  ArrowRight,
} from "lucide-react";

const STEPS = [
  {
    icon: FileText,
    n: "01",
    title: "You Share the Drawing",
    body:
      "Send your drawing, target quantity, materials and delivery destination. NDA-protected. Our engineers review feasibility quickly.",
  },
  {
    icon: GitBranch,
    n: "02",
    title: "We Route to the Right Plant",
    body:
      "Routed to our in-house Tirupati Group facility — or to a pre-audited partner — based on best-fit process, capacity and cost.",
  },
  {
    icon: Factory,
    n: "03",
    title: "Production Under Our Control",
    body:
      "Weekly status calls, milestone gating and progress reporting. You speak to one English-speaking project owner — never the factory.",
  },
  {
    icon: ScanSearch,
    n: "04",
    title: "Inspection in Our Lab",
    body:
      "Every batch — in-house or partner-made — passes through our CMM-equipped quality lab. PPAP / FAI documentation included.",
  },
  {
    icon: PackageCheck,
    n: "05",
    title: "Consolidation & Packing",
    body:
      "Export-grade VCI packing, custom crates, labelling, marking and consolidated documentation under one INCO term.",
  },
  {
    icon: Ship,
    n: "06",
    title: "Door-to-Door Solutions",
    body:
      "Door-to-door solutions to USA, UK, Europe and the Middle East with full customs documentation and tracking.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 lg:py-32 bg-brand-bg overflow-hidden"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="divider-rule" />
              <span className="eyebrow">How We Work — The Bridge Model</span>
            </div>
            <h2 className="h-section">
              One trusted partner.
              <br />
              <span className="text-brand-blue">From your drawing to your dock.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 text-brand-text/75 leading-relaxed">
            International procurement teams face the same recurring barriers in
            India — language, quality consistency, payments and logistics.
            Tirupati Global Sourcing Company (TGSC) removes all of them. We
            become your single English-speaking, accountable partner — managing
            our own factories and a vetted partner network on your behalf.
          </div>
        </div>

        {/* Visual flow chip */}
        <div className="mb-14 hidden lg:flex items-center justify-between gap-3 bg-brand-navy text-white p-5">
          {[
            "Your Drawing",
            "Tirupati Global Sourcing Company",
            "In-House Plant OR Vetted Partner",
            "Quality Lab Inspection",
            "Consolidated Export",
            "Delivered to You",
          ].map((label, i, arr) => (
            <div key={label} className="flex items-center gap-3 flex-1">
              <div className="flex-1 text-center">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-orange">
                  Step {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-1 font-heading font-extrabold text-sm">{label}</div>
              </div>
              {i < arr.length - 1 && (
                <ArrowRight className="h-4 w-4 text-brand-orange shrink-0" />
              )}
            </div>
          ))}
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-12 left-0 right-0 h-px bg-gray-200" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                  data-testid={`service-step-${i}`}
                  className="relative"
                >
                  <div className="hidden lg:flex absolute -top-1.5 left-0 right-0 justify-center">
                    <span className="h-3 w-3 rounded-full bg-brand-orange step-active" />
                  </div>
                  <div className="lg:pt-12">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 flex items-center justify-center bg-brand-navy text-white">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="font-heading font-extrabold text-2xl text-brand-blue/30 leading-none">
                        {s.n}
                      </div>
                    </div>
                    <h3 className="mt-4 font-heading font-extrabold text-lg text-brand-navy leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm text-brand-text/70 leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
